import { useState, type FormEvent } from 'react'
import { supabase } from '../lib/supabase'

/**
 * A FORGOTTEN PASSWORD, WITHOUT THE SQL EDITOR
 * --------------------------------------------
 * Until today the answer to "I've forgotten my password" was: somebody opens
 * the Supabase SQL editor and runs an UPDATE with crypt(). That is not an
 * answer, it is a reason the system can only have one user.
 *
 * TWO ROUTES ON PURPOSE, because email is not a safe assumption. A field
 * officer in Kouk Thlok may have an email address that exists only because an
 * administrator invented one to create their login, and may never read it. So
 * the screen offers the email route AND tells them plainly what to do instead —
 * ask their administrator, who can set a new password in seconds.
 *
 * Nothing here reveals whether an address is registered. Supabase deliberately
 * returns success either way, and this screen says the same thing either way,
 * because "no account with that email" is a way to find out who works here.
 */

export function ForgotPasswordScreen({ onBack }: { onBack: () => void }) {
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)

    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      // Back to the app itself. Supabase appends a recovery token, and App.tsx
      // notices it and shows the "choose a new password" screen.
      redirectTo: window.location.origin,
    })

    setBusy(false)

    if (error) {
      // A rate limit is the common one on the free tier, and it is worth saying
      // plainly rather than hiding behind "something went wrong".
      setError(
        /rate|limit|too many/i.test(error.message)
          ? 'Too many reset emails have been sent recently. Wait an hour, or ask your administrator to set a new password for you.'
          : error.message,
      )
      return
    }
    setSent(true)
  }

  return (
    <div className="screen">
      <div className="appbar">
        <h1>Forgotten password</h1>
        <button className="btn-quiet" onClick={onBack}>
          Back
        </button>
      </div>

      <div className="center-card">
        <div className="card">
          {!sent && (
            <form onSubmit={onSubmit}>
              <h2 style={{ marginTop: 0 }}>Get a reset link</h2>

              <label htmlFor="e">Your email</label>
              <input
                id="e"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                required
              />

              {error && (
                <div className="alert alert-error" role="alert">
                  {error}
                </div>
              )}

              <div style={{ marginTop: 18 }}>
                <button className="btn-primary" type="submit" disabled={busy || !email}>
                  {busy ? 'Sending…' : 'Send the link'}
                </button>
              </div>
            </form>
          )}

          {sent && (
            <div
              className="alert"
              style={{
                borderColor: 'var(--green-700)',
                color: 'var(--green-900)',
                background: 'var(--green-100)',
              }}
              role="status"
            >
              <strong>If that address has an account, a link is on its way.</strong>
              <p style={{ marginBottom: 0 }}>
                Open it on this phone and you will be asked to choose a new
                password. The link works once, and expires after an hour.
              </p>
            </div>
          )}

          <hr style={{ margin: '20px 0', border: 0, borderTop: '1px solid var(--line)' }} />

          <h3 style={{ marginTop: 0 }}>No email, or no signal?</h3>
          <p className="muted" style={{ marginBottom: 0 }}>
            Ask your administrator. They can set a new password for you in under
            a minute, and tell you what it is in person. That route needs no
            email and no internet on your phone — which in a village is usually
            the faster answer anyway.
          </p>
        </div>
      </div>
    </div>
  )
}
